import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faFilm, faTv, faStar } from '@fortawesome/free-solid-svg-icons';
import { faStar as farStar } from '@fortawesome/free-regular-svg-icons';
import MediaModal from '../components/MediaModal'; // <-- unified modal
import { fetchMovieDetails, fetchContentDetails } from '../services/tmdb';

// ========== TRANSLATIONS ==========
const translations = {
  en: {
    yourWatchlist: 'YOUR WATCHLIST',
    movies: 'Movies',
    series: 'Series',
    noMoviesInWatchlist: 'No movies in your watchlist',
    noSeriesInWatchlist: 'No series in your watchlist',
  },
  fr: {
    yourWatchlist: 'VOTRE LISTE DE SUIVI',
    movies: 'Films',
    series: 'Séries',
    noMoviesInWatchlist: 'Aucun film dans votre liste de suivi',
    noSeriesInWatchlist: 'Aucune série dans votre liste de suivi',
  },
  es: {
    yourWatchlist: 'TU LISTA DE SEGUIMIENTO',
    movies: 'Películas',
    series: 'Series',
    noMoviesInWatchlist: 'No hay películas en tu lista de seguimiento',
    noSeriesInWatchlist: 'No hay series en tu lista de seguimiento',
  },
  it: {
    yourWatchlist: 'LA TUA LISTA',
    movies: 'Film',
    series: 'Serie TV',
    noMoviesInWatchlist: 'Nessun film nella tua lista',
    noSeriesInWatchlist: 'Nessuna serie nella tua lista',
  },
  nl: {
    yourWatchlist: 'JOUW WATCHLIST',
    movies: 'Films',
    series: 'Series',
    noMoviesInWatchlist: 'Geen films in je watchlist',
    noSeriesInWatchlist: 'Geen series in je watchlist',
  },
};

const ContentFilter = ({ activeFilter, setActiveFilter, lang }) => {
  const t = (key) => {
    const langData = translations[lang] || translations.en;
    return langData[key] || key;
  };

  return (
    <div className="content-filter">
      <button 
        className={`filter-btn ${activeFilter === 'movies' ? 'active' : ''}`}
        onClick={() => setActiveFilter('movies')}
      >
        <FontAwesomeIcon icon={faFilm} />
        {t('movies')}
      </button>
      <div className="filter-divider"></div>
      <button 
        className={`filter-btn ${activeFilter === 'series' ? 'active' : ''}`}
        onClick={() => setActiveFilter('series')}
      >
        <FontAwesomeIcon icon={faTv} />
        {t('series')}
      </button>
    </div>
  );
};

const MediaCard = ({ item, onCardClick, onToggleFavorite, isFavorite }) => {
  const createStarRating = (rating) => {
    const starRating = rating / 2;
    const fullStars = Math.floor(starRating);
    const partialStar = starRating - fullStars;
    
    let starsHTML = [];
    
    for (let i = 0; i < fullStars; i++) {
      starsHTML.push(
        <div className="star" key={`full-${i}`}>
          <FontAwesomeIcon icon={farStar} />
          <div className="star-filled" style={{ width: '100%' }}>
            <FontAwesomeIcon icon={faStar} />
          </div>
        </div>
      );
    }
    
    if (partialStar > 0) {
      starsHTML.push(
        <div className="star" key="partial">
          <FontAwesomeIcon icon={farStar} />
          <div className="star-filled" style={{ width: `${partialStar * 100}%` }}>
            <FontAwesomeIcon icon={faStar} />
          </div>
        </div>
      );
    }
    
    const emptyStars = 5 - Math.ceil(starRating);
    for (let i = 0; i < emptyStars; i++) {
      starsHTML.push(
        <div className="star" key={`empty-${i}`}>
          <FontAwesomeIcon icon={farStar} />
          <div className="star-filled" style={{ width: '0%' }}>
            <FontAwesomeIcon icon={faStar} />
          </div>
        </div>
      );
    }
    
    return starsHTML;
  };

  const posterPath = item.poster_path 
    ? `https://image.tmdb.org/t/p/w500${item.poster_path}`
    : 'https://via.placeholder.com/500x750?text=No+Poster';

  const title = item.title || item.name || 'UNTITLED';
  
  const truncateTitle = (title, maxLength = 12) => {
    if (title.length > maxLength) {
      return title.substring(0, maxLength) + '...';
    }
    return title;
  };

  return (
    <div 
      className="movie-card"
      onClick={() => onCardClick(item)}
    >
      <div className="poster-container">
        <img 
          src={posterPath} 
          alt={title} 
          className="movie-poster" 
          loading="lazy" 
        />
        <button 
          className={`favorite-btn ${isFavorite ? 'active' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite(item);
          }}
        >
          <FontAwesomeIcon icon={faHeart} />
        </button>
      </div>
      <div className="movie-details">
        <div className="movie-title" title={title}>
          {truncateTitle(title).toUpperCase()}
        </div>
        <div className="star-rating">
          {createStarRating(item.vote_average || 0)}
        </div>
      </div>
    </div>
  );
};

const FavoritesPage = () => {
  const [activeFilter, setActiveFilter] = useState('movies');
  const [watchlist, setWatchlist] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState(null); // unified
  const [selectedMediaType, setSelectedMediaType] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  const [searchParams, setSearchParams] = useSearchParams();

  // Language
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('preferredLanguage') || 'en';
  });

  const t = (key) => {
    const langData = translations[language] || translations.en;
    return langData[key] || key;
  };

  // Handle window resize to detect mobile/desktop
  useLayoutEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen to language changes from other tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'preferredLanguage') {
        setLanguage(e.newValue || 'en');
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const storedWatchlist = JSON.parse(localStorage.getItem('watchlist')) || [];
    setWatchlist(storedWatchlist);
  }, []);

  useEffect(() => {
    const movieId = searchParams.get('movie');
    const seriesId = searchParams.get('series');
    
    if (movieId && !isNaN(parseInt(movieId))) {
      openContentModal(parseInt(movieId), 'movie');
    } else if (seriesId && !isNaN(parseInt(seriesId))) {
      openContentModal(parseInt(seriesId), 'tv');
    }
  }, []);

  useEffect(() => {
    const filtered = watchlist.filter(item => 
      activeFilter === 'movies' ? item.type === 'movie' : item.type === 'tv'
    );
    setFilteredItems(filtered);
  }, [watchlist, activeFilter]);

  // ---- Modal functions (unified) ----
  const openContentModal = async (contentId, type) => {
    setIsLoading(true);
    try {
      let details;
      if (type === 'movie') {
        details = await fetchMovieDetails(contentId);
      } else if (type === 'tv') {
        details = await fetchContentDetails('tv', contentId);
      }

      if (details) {
        setSelectedMedia({ ...details, media_type: type });
        setSelectedMediaType(type);
        setIsModalOpen(true);
        
        const newSearchParams = new URLSearchParams(searchParams);
        if (type === 'movie') {
          newSearchParams.set('movie', contentId);
          newSearchParams.delete('series');
        } else {
          newSearchParams.set('series', contentId);
          newSearchParams.delete('movie');
        }
        setSearchParams(newSearchParams);
      }
    } catch (error) {
      console.error('Error fetching content details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCardClick = async (item) => {
    openContentModal(item.id, item.type);
  };

  // ---- Handle media change inside modal (update URL) ----
  const handleMediaChange = (media, mediaType) => {
    setSelectedMedia(media);
    setSelectedMediaType(mediaType);
    const params = new URLSearchParams(searchParams);
    if (mediaType === 'movie') {
      params.set('movie', media.id);
      params.delete('series');
    } else {
      params.set('series', media.id);
      params.delete('movie');
    }
    setSearchParams(params);
  };

  const handleToggleFavorite = (item) => {
    const updatedWatchlist = watchlist.filter(fav => 
      !(fav.id === item.id && fav.type === item.type)
    );
    
    localStorage.setItem('watchlist', JSON.stringify(updatedWatchlist));
    setWatchlist(updatedWatchlist);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMedia(null);
    setSelectedMediaType(null);
    
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('movie');
    newSearchParams.delete('series');
    setSearchParams(newSearchParams);
  };

  const refreshFavorites = () => {
    const updatedWatchlist = JSON.parse(localStorage.getItem('watchlist')) || [];
    setWatchlist(updatedWatchlist);
  };

  const reversedItems = [...filteredItems].reverse();

  // Background images
  const pcBackground = 'https://i.ibb.co/DH0mXVQ1/Untitled-5.png';
  const mobileBackground = 'https://i.ibb.co/FLFDTbdc/Untitled-1.png';

  return (
    <div id="favoritesPage" className="page-content">
      <div 
        className="favorites-page-bg"
        style={{ 
          backgroundImage: `url(${isMobile ? mobileBackground : pcBackground})`
        }}
      ></div>
      <div className="favorites-overlay"></div>
      
      <h1 className="page-title">{t('yourWatchlist')}</h1>
      
      <ContentFilter 
        activeFilter={activeFilter} 
        setActiveFilter={setActiveFilter} 
        lang={language}
      />
      
      {reversedItems.length === 0 ? (
        <div className="empty-page">
          <p>
            {activeFilter === 'movies' 
              ? t('noMoviesInWatchlist')
              : t('noSeriesInWatchlist')}
          </p>
        </div>
      ) : (
        <div className="favorites-grid">
          {reversedItems.map(item => (
            <MediaCard 
              key={`${item.type}-${item.id}`} 
              item={item}
              onCardClick={handleCardClick}
              onToggleFavorite={handleToggleFavorite}
              isFavorite={true}
            />
          ))}
        </div>
      )}

      {isLoading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}

      {/* Unified Media Modal */}
      {isModalOpen && selectedMedia && selectedMediaType && (
        <MediaModal
          media={selectedMedia}
          mediaType={selectedMediaType}
          onClose={closeModal}
          onMediaChange={handleMediaChange}
          onUpdate={refreshFavorites}
        />
      )}
      
      <style jsx>{`
        /* your existing styles */
      `}</style>
    </div>
  );
};

export default FavoritesPage;